import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { useAuth } from '@/hooks/use-auth';
import { useBrandKits } from '@/hooks/useBrandKits';
import { supabase } from '@/integrations/supabase/client';
import { resolveOwnerId } from '@/lib/identity/resolveOwnerId';
import { friendlyVideoError } from '@/lib/video/friendly-error';
import { useVideoDialogStore } from '@/stores/useVideoDialogStore';
import { type VideoJobState, useVideoJobsDataStore } from '@/stores/useVideoJobsDataStore';
import { logger } from '@/utils/logger';

const STATUS_MAP: Record<string, VideoJobState['status']> = {
  queued: 'pending',
  pending: 'pending',
  submitted: 'submitted',
  running: 'processing',
  processing: 'processing',
  uploading: 'uploading',
  completed: 'completed',
  failed: 'failed',
  timeout: 'timeout',
};

function mapStatus(raw: string): VideoJobState['status'] {
  return STATUS_MAP[raw] ?? 'pending';
}

function rowToState(row: any): VideoJobState {
  return {
    jobId: row.id,
    status: mapStatus(row.status),
    videoUrl: row.video_url ?? undefined,
    thumbnailUrl: row.cover_image_url ?? row.source_image_url ?? undefined,
    errorMessage: row.error_message ?? undefined,
    createdAt: row.created_at ?? undefined,
    completedAt: row.completed_at ?? undefined,
  };
}

const STUCK_JOB_MS = 10 * 60 * 1000;

/**
 * `video_jobs` is account-scoped AND carries `brand_kit_id` — same shape as
 * `projects`/`social_posts`. Legacy rows with `brand_kit_id IS NULL` stay
 * visible app-wide by deliberate convention (see CLAUDE.md), so this is an
 * OR-null check, never a strict equality: a strict `.eq()` here would drop
 * every pre-workspace-stamping job the moment a user switched into any
 * workspace. `activeKitId === null` (no workspace resolved yet) skips the
 * check entirely rather than hiding everything.
 */
function belongsToActiveWorkspace(
  recordBrandKitId: string | null | undefined,
  activeKitId: string | null,
): boolean {
  if (!activeKitId) return true;
  if (!recordBrandKitId) return true;
  return recordBrandKitId === activeKitId;
}

export function useGlobalJobMonitor() {
  const { user } = useAuth();
  const { activeKit, isActiveKitPending } = useBrandKits();
  const activeKitId = activeKit?.id ?? null;
  const openVideoDialog = useVideoDialogStore((s) => s.open);
  const navigate = useNavigate();
  const trackedJobs = useRef<Set<string>>(new Set());
  const stuckTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  // F-124: jobs we've already attempted a client poster-extraction for this
  // session, so a re-fired completion event doesn't re-extract.
  const posterExtractedJobs = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!user) return;
    // Mirrors useInfiniteRecentChatSessions's `enabled: !isActiveKitPending`:
    // while the pointer is still resolving, `activeKitId` may be a guessed
    // account default rather than the real target, and fetching/subscribing
    // on that guess would flash another workspace's jobs before
    // self-correcting once the real pointer lands.
    if (isActiveKitPending) return;

    let cancelled = false;

    function trackActive(jobId: string, prompt?: string | null, createdAt?: string | null) {
      if (trackedJobs.current.has(jobId)) return;
      // RC-6: a postgres_changes UPDATE for a job created hours ago (stuck
      // upload from a prior session, app crash mid-poll, etc.) was spawning
      // a fresh "Generating video…" toast even though the job is stale. Skip
      // anything older than 5 minutes — real in-flight Veo runs are <3 min.
      if (createdAt) {
        const ageMs = Date.now() - new Date(createdAt).getTime();
        if (Number.isFinite(ageMs) && ageMs > 5 * 60 * 1000) return;
      }
      trackedJobs.current.add(jobId);
      toast.loading('Generating video…', {
        id: `video-job-${jobId}`,
        description: prompt
          ? prompt.length > 60
            ? prompt.slice(0, 60) + '…'
            : prompt
          : 'This usually takes 60–180 seconds.',
        action: {
          label: 'Dismiss',
          onClick: () => {
            toast.dismiss(`video-job-${jobId}`);
            clearStuckTimer(jobId);
            trackedJobs.current.delete(jobId);
          },
        },
      });
      const timer = setTimeout(() => {
        if (!trackedJobs.current.has(jobId)) return;
        toast.loading('Still generating…', {
          id: `video-job-${jobId}`,
          description: 'Veo is still rendering your video.',
          action: {
            label: 'Dismiss',
            onClick: () => {
              toast.dismiss(`video-job-${jobId}`);
              clearStuckTimer(jobId);
              trackedJobs.current.delete(jobId);
            },
          },
        });
      }, STUCK_JOB_MS);
      stuckTimers.current.set(jobId, timer);
    }

    function clearStuckTimer(jobId: string) {
      const timer = stuckTimers.current.get(jobId);
      if (timer) {
        clearTimeout(timer);
        stuckTimers.current.delete(jobId);
      }
    }

    const fetchActiveJobs = async (ownerId: string, kitId: string | null) => {
      // Stale-job cutoff: jobs older than 15 min that are still in a pending
      // state are effectively dead (failed Veo runs whose status flip never
      // committed, race conditions, app crashes mid-poll). Showing
      // "Generating video..." for them on every page load is noise. Real
      // in-flight jobs are <3 min on the happy path.
      const cutoff = new Date(Date.now() - 15 * 60 * 1000).toISOString();
      let query = supabase
        .from('video_jobs')
        .select(
          'id, prompt, created_at, status, video_url, cover_image_url, error_message, source_image_url, completed_at, brand_kit_id',
        )
        .eq('user_id', ownerId)
        .in('status', ['queued', 'running', 'uploading', 'pending', 'submitted'])
        .gte('created_at', cutoff);
      // Same pattern as useInfiniteRecentChatSessions / mediaService: scope
      // to the active workspace, keeping unattributed (legacy) rows visible.
      if (kitId) {
        query = query.or(`brand_kit_id.eq.${kitId},brand_kit_id.is.null`);
      }
      const { data } = await query;

      if (cancelled || !data) return;
      for (const job of data) {
        useVideoJobsDataStore.getState().setJob(job.id, rowToState(job));
        trackActive(job.id, job.prompt, job.created_at);
      }
    };

    let channel: ReturnType<typeof supabase.channel> | null = null;

    void (async () => {
      // video_jobs and projects RLS both gate on current_account_owner_id(),
      // so rows are stamped with the account owner's id, not the raw session
      // id — a shared-account member's `user.id` matches zero rows here.
      // resolveOwnerId() now fails closed (throws) instead of silently
      // falling back to the raw id, so this bare await needs a try/catch —
      // skip the monitor setup rather than let it become an unhandled
      // rejection that also aborts the realtime subscription below.
      let ownerId: string;
      try {
        ownerId = await resolveOwnerId(user.id);
      } catch (err) {
        logger.error('useGlobalJobMonitor: failed to resolve account owner id', err);
        return;
      }
      if (cancelled) return;

      void fetchActiveJobs(ownerId, activeKitId);

      // Pass 10 P1 fix: per-mount UUID suffix to dodge channel-cache reuse on
      // StrictMode/HMR remounts. The static name 'global-video-jobs' would
      // otherwise collide on second mount, throwing "cannot add postgres_changes
      // callbacks ... after subscribe()" and silently breaking the global
      // "Generating video..." toast lifecycle.
      channel = supabase
        .channel(`global-video-jobs-${crypto.randomUUID()}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'video_jobs',
            filter: `user_id=eq.${ownerId}`,
          },
          (payload: any) => {
            if (cancelled) return;
            const record = payload.new || payload.old;
            const status: string = record.status;
            const jobId: string = record.id;

            // Pass 13 §2.5 — dev-only event log so realtime publication
            // flow is observable end-to-end. Catches the "events never
            // arrive" failure mode that Pass 9's publication migration
            // was supposed to fix but couldn't be confirmed without a
            // 5-minute video job round-trip.
            // F-028 (UAT 2026-05-19) — this is dev observability telemetry,
            // not an error. Using console.error made every realtime job event
            // surface as a red console error (16+ per video turn during the
            // F-029 repro), drowning out genuine errors. Route through the
            // project logger at debug level instead.
            if (import.meta.env.DEV) {
              logger.debug(
                `[useGlobalJobMonitor] postgres_changes ${payload.eventType} job=${jobId} status=${status}`,
              );
            }

            if (!status) return;
            // Realtime `filter:` only supports a single equality predicate,
            // so it can't express the OR-null workspace check the initial
            // fetch above uses — apply it client-side instead. A job stamped
            // to a DIFFERENT workspace than the one currently active must not
            // toast or populate the store: that is the exact "previous
            // client's data after a switch" bug this hook exists to avoid.
            if (!belongsToActiveWorkspace(record.brand_kit_id, activeKitId)) return;
            useVideoJobsDataStore.getState().setJob(jobId, rowToState(record));

            const isPending = ['queued', 'running', 'uploading', 'pending', 'submitted'].includes(
              status,
            );
            const isUploading = status === 'uploading';
            const isCompleted = status === 'completed';
            const isFailed = status === 'failed';

            if (isPending) {
              if (isUploading && trackedJobs.current.has(jobId)) {
                toast.loading('Uploading to library…', {
                  id: `video-job-${jobId}`,
                  description: 'Almost done.',
                });
              } else {
                trackActive(jobId, record.prompt, record.created_at);
              }
            } else if (isCompleted) {
              clearStuckTimer(jobId);
              trackedJobs.current.delete(jobId);

              // F-124: auto-generate a poster for the just-completed video so the
              // Library (and any preview) shows a real first-frame thumbnail
              // instead of an icon. Server-side ffmpeg extraction frequently
              // returns null, and the client canvas fallback previously only ran
              // when the user happened to OPEN the Video Studio — so most videos
              // never got a poster (14/66 in prod). Do it here, on completion,
              // for every job the user generates. Best-effort + fire-and-forget:
              // any failure leaves the current (icon) behaviour untouched.
              if (
                record.video_url &&
                !record.cover_image_url &&
                !posterExtractedJobs.current.has(jobId)
              ) {
                posterExtractedJobs.current.add(jobId);
                void (async () => {
                  try {
                    const { extractClientPoster } =
                      await import('@/lib/video/extract-client-poster');
                    const blob = await extractClientPoster(record.video_url, {
                      atSeconds: 1.0,
                      widthPx: 720,
                    });
                    if (!blob) return;
                    const posterBase64 = await new Promise<string>((resolve) => {
                      const reader = new FileReader();
                      reader.onloadend = () => resolve(String(reader.result ?? ''));
                      reader.readAsDataURL(blob);
                    });
                    if (!posterBase64) return;
                    // Fresh token (avoid the F-104 stale-closure trap — this
                    // callback is created once at subscribe time).
                    const {
                      data: { session: fresh },
                    } = await supabase.auth.getSession();
                    await fetch('/api/video-cover', {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${fresh?.access_token ?? ''}`,
                      },
                      body: JSON.stringify({ jobId, posterBase64 }),
                    });
                  } catch {
                    // best-effort; poster is non-critical
                  }
                })();
              }
              // RC-2: when a workflow's request_user_approval timed out the
              // 90s in-tool poll, pending_artifact has awaiting_render=true
              // and the placeholder source-image URL. Now that the video is
              // ready, sweep workflow_state.pending_artifact (and
              // last_approved_artifact if the user already approved) to the
              // real video URL so step 4's create_content_batch wraps the
              // actual MP4 instead of the source frame.
              void (async () => {
                try {
                  const { data: rows } = await supabase
                    .from('projects')
                    .select('id, workflow_state')
                    .eq('user_id', ownerId)
                    .or(
                      `workflow_state->pending_artifact->>videoJobId.eq.${jobId},workflow_state->last_approved_artifact->>videoJobId.eq.${jobId}`,
                    );
                  if (!rows?.length) return;
                  for (const row of rows) {
                    const ws = (row.workflow_state ?? null) as Record<string, unknown> | null;
                    if (!ws) continue;
                    const next = { ...ws } as Record<string, unknown>;
                    const pending = ws.pending_artifact as Record<string, unknown> | null;
                    if (
                      pending &&
                      typeof pending === 'object' &&
                      (pending as { videoJobId?: string }).videoJobId === jobId
                    ) {
                      next.pending_artifact = {
                        ...pending,
                        url: record.video_url,
                        videoUrl: record.video_url,
                        posterUrl: record.cover_image_url ?? undefined,
                        awaiting_render: false,
                        resolved_at: new Date().toISOString(),
                      };
                    }
                    const approved = ws.last_approved_artifact as Record<string, unknown> | null;
                    if (
                      approved &&
                      typeof approved === 'object' &&
                      (approved as { videoJobId?: string }).videoJobId === jobId
                    ) {
                      next.last_approved_artifact = {
                        ...approved,
                        url: record.video_url,
                        videoUrl: record.video_url,
                        posterUrl: record.cover_image_url ?? undefined,
                        resolved_at: new Date().toISOString(),
                      };
                    }
                    // Also sweep history[] entries that captured the jobId.
                    if (Array.isArray(ws.history)) {
                      next.history = (ws.history as Array<Record<string, unknown>>).map((h) => {
                        const arts = (h.artifacts ?? []) as Array<Record<string, unknown>>;
                        if (!Array.isArray(arts) || arts.length === 0) return h;
                        const swept = arts.map((a) =>
                          (a as { videoJobId?: string }).videoJobId === jobId
                            ? { ...a, url: record.video_url, videoUrl: record.video_url }
                            : a,
                        );
                        return { ...h, artifacts: swept };
                      });
                    }
                    await supabase
                      .from('projects')
                      .update({ workflow_state: next })
                      .eq('id', row.id);
                  }
                } catch {
                  /* swallow — toast already shows success */
                }
              })();
              const lineageSessionId: string | null = record.source_session_id ?? null;
              if (lineageSessionId) {
                toast.success('Video ready in chat', {
                  id: `video-job-${jobId}`,
                  description: 'Open the chat to view + edit.',
                  duration: 8000,
                  action: {
                    label: 'Open chat',
                    onClick: () => {
                      if (lineageSessionId === 'home') {
                        navigate('/');
                      } else {
                        navigate(`/?session=${encodeURIComponent(lineageSessionId)}`);
                      }
                    },
                  },
                });
              } else {
                toast.success('Your video is ready', {
                  id: `video-job-${jobId}`,
                  description: 'Saved to your Media Library.',
                  duration: 8000,
                  action: {
                    label: 'View',
                    onClick: () => {
                      openVideoDialog({
                        jobId: record.id,
                        videoUrl: record.video_url ?? '',
                        posterUrl: record.cover_image_url ?? null,
                        prompt: record.prompt ?? '',
                        model: record.model ?? '',
                        duration: record.duration_seconds ?? 6,
                        aspectRatio: record.aspect_ratio ?? '9:16',
                        source: { kind: 'library' },
                        thumbnailUrl: record.cover_image_url ?? undefined,
                      });
                    },
                  },
                });
              }
            } else if (isFailed) {
              clearStuckTimer(jobId);
              trackedJobs.current.delete(jobId);
              const errorMsg = friendlyVideoError(record.error_message);
              toast.error('Video generation failed', {
                id: `video-job-${jobId}`,
                description: errorMsg,
                duration: 8000,
                action: {
                  label: 'Try again',
                  onClick: () => {
                    const prompt = record.prompt ?? '';
                    navigate(`/?prefill=${encodeURIComponent(prompt)}&mode=video`);
                  },
                },
              });
            }
          },
        )
        .subscribe();
    })();

    // Capture the ref value so cleanup uses the Map that was live when the
    // effect ran, not a potentially-replaced ref on a later render.
    const timersAtCleanup = stuckTimers.current;
    return () => {
      cancelled = true;
      timersAtCleanup.forEach(clearTimeout);
      timersAtCleanup.clear();
      if (channel) void supabase.removeChannel(channel);
    };
    // activeKitId (and isActiveKitPending) are load-bearing deps, not
    // incidental — this is the "query key" for a subscription-based hook:
    // a workspace switch must tear down the old channel/fetch and stand up a
    // fresh one scoped to the new workspace, exactly as a TanStack Query key
    // change would trigger a refetch. See belongsToActiveWorkspace's doc.
  }, [user, openVideoDialog, navigate, activeKitId, isActiveKitPending]);
}
